import { type Dispatch, type ReactNode, type SetStateAction, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import UserLayout from '@/components/Layout/UserLayout';
import { API_DOMAIN, trpc } from '@/api/trpc';
import { Alert, Badge, Button, FormInput, FormSelect } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { showToast } from '@/components/Layout/Toast';
import { useAuth } from '@/context/AuthContext';
import SupportRestrictedFeatureCard from '@/components/Support/SupportRestrictedFeatureCard';
import type { AppRouter } from '@/types/appRouter';
import { getDate } from '@einhaus/meteoric-helpers';
import { FaCheckCircle, FaCloud, FaCode, FaCopy, FaDownload, FaExternalLinkAlt, FaKey, FaPlus, FaTerminal, FaTrash } from 'react-icons/fa';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type McpTokenRecord = RouterOutputs['user']['listMcpAccessTokens']['tokens'][number];
type McpClientType = McpTokenRecord['clientType'];
type McpClientReadiness = RouterOutputs['user']['listMcpClientReadiness']['clients'][number];
type McpConnectorMetadata = RouterOutputs['user']['listMcpConnectorMetadata']['connectors'][number];

const EXPIRATION_OPTIONS = [
    { value: '90', label: '90 days' },
    { value: '30', label: '30 days' },
    { value: '180', label: '180 days' },
    { value: '365', label: '1 year' },
    { value: 'never', label: 'No expiry' }
];

const CLIENT_OPTIONS: { value: McpClientType; label: string; defaultName: string; description: string }[] = [
    {
        value: 'codex',
        label: 'Codex',
        defaultName: 'Codex MCP',
        description: 'Local Codex CLI and IDE extension setup.'
    },
    {
        value: 'claudeCode',
        label: 'Claude Code',
        defaultName: 'Claude Code MCP',
        description: 'Local Claude Code user-scope MCP setup.'
    },
    {
        value: 'custom',
        label: 'Codex + Claude Code',
        defaultName: 'Project314 MCP',
        description: 'Generate a reusable local setup script for both supported coding tools.'
    },
    {
        value: 'chatgpt',
        label: 'ChatGPT App',
        defaultName: 'ChatGPT MCP',
        description: 'Remote connector setup; OAuth connector support is required for production use.'
    },
    {
        value: 'claude',
        label: 'Claude (Web & Desktop)',
        defaultName: 'Claude MCP',
        description:
            'Remote connector setup shared by Claude web, Desktop, and mobile; OAuth connector support is required for production use.'
    }
];

const LOCAL_CLIENT_TYPES = new Set<McpClientType>(['codex', 'claudeCode', 'custom']);
const LOCAL_CLIENT_OPTIONS = CLIENT_OPTIONS.filter((option) => LOCAL_CLIENT_TYPES.has(option.value));
const LOCAL_CLIENT_SELECT_OPTIONS = LOCAL_CLIENT_OPTIONS.map((option) => ({ value: option.value, label: option.label }));
const CODEX_TOKEN_ENV_VAR = 'PROJECT314_CODEX_MCP_TOKEN';
const CLAUDE_CODE_TOKEN_ENV_VAR = 'PROJECT314_CLAUDE_CODE_MCP_TOKEN';
const SHARED_TOKEN_ENV_VAR = 'PROJECT314_MCP_TOKEN';
const TOKEN_ENV_FILE = '~/.project314-mcp.env';

const TOKEN_ENV_VARS_BY_CLIENT: Record<McpClientType, string[]> = {
    codex: [CODEX_TOKEN_ENV_VAR],
    claudeCode: [CLAUDE_CODE_TOKEN_ENV_VAR],
    custom: [CODEX_TOKEN_ENV_VAR, CLAUDE_CODE_TOKEN_ENV_VAR, SHARED_TOKEN_ENV_VAR],
    chatgpt: [SHARED_TOKEN_ENV_VAR],
    claude: [SHARED_TOKEN_ENV_VAR]
};

function shellSingleQuote(value: string) {
    return `'${value.replaceAll("'", "'\\''")}'`;
}

function formatDateTime(value: string | null | undefined) {
    return value ?? 'Never';
}

function getClientLabel(clientType: McpClientType | null | undefined) {
    return CLIENT_OPTIONS.find((option) => option.value === clientType)?.label ?? 'Custom';
}

function parseExpiresInDays(expiresInDays: string) {
    return expiresInDays === 'never' ? null : Number(expiresInDays);
}

function isTokenExpired(token: { expiresDateTime: string | null | undefined }, nowDateTime: string) {
    return token.expiresDateTime !== null && token.expiresDateTime !== undefined && token.expiresDateTime <= nowDateTime;
}

function getLocalClientTargets(clientType: McpClientType) {
    return {
        installCodex: clientType === 'codex' || clientType === 'custom',
        installClaude: clientType === 'claudeCode' || clientType === 'custom'
    };
}

function getTokenEnvVars(clientType: McpClientType) {
    return TOKEN_ENV_VARS_BY_CLIENT[clientType];
}

function buildExportLine(envVar: string, token: string) {
    return `export ${envVar}=${shellSingleQuote(token)}`;
}

function buildTokenEnvironmentCommand(token: string, clientType: McpClientType) {
    return getTokenEnvVars(clientType)
        .map((envVar) => buildExportLine(envVar, token))
        .join('\n');
}

function buildClaudeCommand(endpoint: string) {
    // Single quotes are required: the shell must not expand the variable here. Claude Code stores the literal
    // `${VAR}` in ~/.claude.json and resolves it at runtime, so rotating the env file rotates the credential.
    return `claude mcp add --transport http --scope user project314 ${endpoint} --header 'Authorization: Bearer \${${CLAUDE_CODE_TOKEN_ENV_VAR}}'`;
}

function buildCodexCommand(endpoint: string) {
    return `codex mcp add project314 --url ${endpoint} --bearer-token-env-var ${CODEX_TOKEN_ENV_VAR}`;
}

function buildCodexConfig(endpoint: string) {
    return ['[mcp_servers.project314]', `url = "${endpoint}"`, `bearer_token_env_var = "${CODEX_TOKEN_ENV_VAR}"`].join('\n');
}

function buildSetupScript(params: { endpoint: string; token: string; clientType: McpClientType }) {
    const { installCodex, installClaude } = getLocalClientTargets(params.clientType);
    const shellExpansion = (content: string) => `${String.fromCharCode(36)}{${content}}`;
    const homeRef = shellExpansion('HOME');
    const envFileRef = shellExpansion('ENV_FILE');
    const profileRef = shellExpansion('PROFILE');
    const shellProfileRef = shellExpansion(`SHELL_PROFILE:-${homeRef}/.zshrc`);
    const claudeTokenRef = shellExpansion(CLAUDE_CODE_TOKEN_ENV_VAR);

    const scriptLines = [
        'set -euo pipefail',
        '',
        `ENV_FILE="${homeRef}/.project314-mcp.env"`,
        `PROFILE="${shellProfileRef}"`,
        '',
        `touch "${envFileRef}"`,
        `chmod 600 "${envFileRef}"`,
        '',
        "# Replace only this client's export line so other clients keep their own tokens.",
        'upsert_env_var() {',
        '  upsert_var_name="$1"',
        '  upsert_export_line="$2"',
        `  upsert_tmp_file="${envFileRef}.$$"`,
        `  grep -v "^export ${shellExpansion('upsert_var_name')}=" "${envFileRef}" > "${shellExpansion('upsert_tmp_file')}" || true`,
        `  printf '%s\\n' "${shellExpansion('upsert_export_line')}" >> "${shellExpansion('upsert_tmp_file')}"`,
        `  mv "${shellExpansion('upsert_tmp_file')}" "${envFileRef}"`,
        `  chmod 600 "${envFileRef}"`,
        '}'
    ];

    for (const envVar of getTokenEnvVars(params.clientType)) {
        scriptLines.push('', `upsert_env_var ${envVar} ${shellSingleQuote(buildExportLine(envVar, params.token))}`);
    }

    scriptLines.push(
        '',
        `if ! grep -q ".project314-mcp.env" "${profileRef}" 2>/dev/null; then`,
        '  {',
        '    echo ""',
        '    echo "# Project314 MCP token"',
        `    echo "source \\"${envFileRef}\\""`,
        `  } >> "${profileRef}"`,
        'fi',
        '',
        'set +u',
        `source "${envFileRef}"`,
        'set -u'
    );

    if (installCodex) {
        scriptLines.push(
            '',
            'if command -v codex >/dev/null 2>&1; then',
            `  codex mcp add project314 --url ${shellSingleQuote(params.endpoint)} --bearer-token-env-var ${CODEX_TOKEN_ENV_VAR}`,
            'else',
            '  echo "codex command not found; install Codex first, then re-run this script." >&2',
            'fi'
        );
    }

    if (installClaude) {
        scriptLines.push(
            '',
            'if command -v claude >/dev/null 2>&1; then',
            // The header is single quoted on purpose: Claude Code stores the literal reference and expands it at runtime.
            `  claude mcp add --transport http --scope user project314 ${shellSingleQuote(params.endpoint)} --header ${shellSingleQuote(`Authorization: Bearer ${claudeTokenRef}`)}`,
            'else',
            '  echo "claude command not found; install Claude Code first, then re-run this script." >&2',
            'fi'
        );
    }

    scriptLines.push('', 'echo "Project314 MCP setup complete. Restart your coding tool so it picks up the token environment."');

    return ["bash <<'PROJECT314_MCP_SETUP'", ...scriptLines, 'PROJECT314_MCP_SETUP'].join('\n');
}

function buildScorecardLaunchPrompt() {
    return [
        'Use the Project314 MCP server to generate a scorecard from Parameter Discovery.',
        'First call project314_ai_work_list_sessions to find the scorecard_generation session, then project314_ai_work_get_session.',
        'Use project314_scorecard_generation_get_session, claim all feature batches, submit structured reviews, then call project314_scorecard_generation_get_planning_bundle.',
        'Inspect bucketCandidates and selectorGroupCandidates: group credible same-role values with selectorValues, use separate same-feature boost/penalty/veto rules when opposite-role buckets are credible, and follow suggestedAction=split_distinct_rules for credible same-feature range buckets.',
        'Create competing compact, broad, and penalty/veto-aware drafts, evaluate every draft, recommend the strongest draft, and ask me before creating the scorecard.'
    ].join('\n');
}

async function copyText(text: string, label: string) {
    await navigator.clipboard.writeText(text);
    showToast(`${label} copied`, 'success');
}

function downloadTextFile(fileName: string, text: string) {
    const blob = new Blob([`#!/usr/bin/env bash\n${text}\n`], { type: 'text/x-shellscript' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
}

type SetupStep = {
    label: string;
    description: string;
    complete: boolean;
};

function buildSetupSteps(params: { setupToken: McpTokenRecord | null; setupConnected: boolean }): SetupStep[] {
    const { setupToken, setupConnected } = params;

    return [
        {
            label: 'Create token',
            description: setupToken
                ? `${getClientLabel(setupToken.clientType)} token ${setupToken.tokenPrefix}`
                : 'Generate a scoped MCP token.',
            complete: Boolean(setupToken)
        },
        {
            label: 'Install client',
            description: setupToken
                ? setupConnected
                    ? 'Client has connected at least once.'
                    : 'Run the setup command or local setup script.'
                : 'Choose Codex or Claude Code and generate a token.',
            complete: setupConnected
        },
        {
            label: 'Use Project314 tools',
            description: setupConnected
                ? `Last used ${formatDateTime(setupToken?.lastUsedDateTime)}`
                : 'The first MCP request will mark this setup connected.',
            complete: setupConnected
        }
    ];
}

function getTokenStatusLabel(token: McpTokenRecord, nowDateTime: string) {
    if (isTokenExpired(token, nowDateTime)) return 'Expired';
    if (token.firstUsedDateTime || token.lastUsedDateTime) return 'Connected';
    return 'Waiting';
}

function CreateMcpTokenPanel(props: {
    clientType: McpClientType;
    createTokenPending: boolean;
    expiresInDays: string;
    tokenName: string;
    onClientTypeChange: (clientType: McpClientType) => void;
    onCreateToken: () => void;
    onExpiresInDaysChange: (expiresInDays: string) => void;
    onTokenNameChange: (tokenName: string) => void;
}) {
    return (
        <div className="mb-6 rounded border border-zinc-700 bg-[#1d1d1d] p-4">
            <div className="mb-4 flex flex-col gap-1">
                <div className="flex items-center gap-2 text-white">
                    <FaKey className="h-4 w-4 text-zinc-300" />
                    <h2 className="text-lg font-semibold">Create Local MCP Token</h2>
                </div>
                <p className="text-sm text-zinc-400">
                    Local bearer tokens are for Codex, Claude Code, Cursor, and custom developer clients. Hosted ChatGPT and Claude (web,
                    Desktop, and mobile) use the connector setup below.
                </p>
            </div>
            <div className="grid gap-3 md:grid-cols-[190px_minmax(0,1fr)_180px_auto] md:items-end">
                <FormSelect
                    label="Client"
                    options={LOCAL_CLIENT_SELECT_OPTIONS}
                    value={props.clientType}
                    onChange={(event) => props.onClientTypeChange(event.target.value as McpClientType)}
                />
                <FormInput
                    label="Name"
                    maxLength={120}
                    value={props.tokenName}
                    onChange={(event) => props.onTokenNameChange(event.target.value)}
                />
                <FormSelect
                    label="Expiry"
                    options={EXPIRATION_OPTIONS}
                    value={props.expiresInDays}
                    onChange={(event) => props.onExpiresInDaysChange(event.target.value)}
                />
                <Button icon={<FaPlus />} isLoading={props.createTokenPending} onClick={props.onCreateToken}>
                    Generate
                </Button>
            </div>
        </div>
    );
}

function McpSetupPanel(props: {
    connectedTokensCount: number;
    dismissSetupPending: boolean;
    setupDismissed: boolean;
    setupSteps: SetupStep[];
    setupToken: McpTokenRecord | null;
    shouldShowSetupGuide: boolean;
    onDismissSetup: (tokenId: number) => void;
}) {
    const setupTokenId = props.setupToken?.id;

    const statusMessage =
        props.connectedTokensCount > 0
            ? 'At least one MCP client has connected. Setup prompts stay quiet once a token is used.'
            : props.setupDismissed
              ? 'Setup guide hidden for this token.'
              : 'Generate a token, install it locally, then make one MCP request to mark the setup connected.';

    return (
        <div className="mb-6 rounded border border-zinc-700 bg-[#1d1d1d] p-4">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <div className="flex items-center gap-2 text-white">
                        <FaTerminal className="h-4 w-4 text-zinc-300" />
                        <h2 className="text-lg font-semibold">MCP Setup</h2>
                    </div>
                    <p className="mt-1 text-sm text-zinc-400">{statusMessage}</p>
                </div>
                {setupTokenId !== undefined && props.shouldShowSetupGuide && (
                    <Button
                        isLoading={props.dismissSetupPending}
                        size="sm"
                        variant="secondary"
                        onClick={() => props.onDismissSetup(setupTokenId)}
                    >
                        Hide Guide
                    </Button>
                )}
            </div>

            <div className="grid gap-3 md:grid-cols-3">
                {props.setupSteps.map((step, index) => (
                    <div className="rounded border border-zinc-700 bg-[#242424] p-3" key={step.label}>
                        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-white">
                            {step.complete ? (
                                <FaCheckCircle className="h-4 w-4 text-emerald-400" />
                            ) : (
                                <span className="flex h-4 w-4 items-center justify-center rounded-full border border-zinc-500 text-[10px] text-zinc-300">
                                    {index + 1}
                                </span>
                            )}
                            {step.label}
                        </div>
                        <div className="text-xs leading-5 text-zinc-400">{step.description}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CreatedTokenPanel(props: {
    claudeCommand: string;
    codexSetup: string;
    createdToken: string | null;
    setupScript: string;
    setupToken: McpTokenRecord | null;
    tokenEnvironmentCommand: string;
}) {
    if (!props.createdToken || !props.setupToken) return null;

    const clientType = props.setupToken.clientType;
    const showLocalSetup = LOCAL_CLIENT_TYPES.has(clientType);
    const { installCodex, installClaude } = getLocalClientTargets(clientType);

    return (
        <Alert className="mb-6 border-[#2f6f5f] bg-[#16382f] text-white" variant="success">
            <div className="space-y-4">
                <div>
                    <div className="mb-1 text-sm font-semibold">New {getClientLabel(clientType)} token</div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <code className="min-w-0 flex-1 break-all rounded bg-black/30 px-3 py-2 text-xs">{props.createdToken}</code>
                        <Button icon={<FaCopy />} size="sm" variant="light" onClick={() => copyText(props.createdToken ?? '', 'Token')}>
                            Copy
                        </Button>
                    </div>
                </div>

                {showLocalSetup && <LocalSetupScriptPanel setupScript={props.setupScript} />}

                <div className="grid gap-3 lg:grid-cols-3">
                    <CopyableCommandPanel
                        icon={<FaKey className="h-3.5 w-3.5" />}
                        label="Token environment"
                        text={props.tokenEnvironmentCommand}
                    />
                    {(!showLocalSetup || installClaude) && (
                        <CopyableCommandPanel icon={<FaCode className="h-3.5 w-3.5" />} label="Claude Code" text={props.claudeCommand} />
                    )}
                    {(!showLocalSetup || installCodex) && (
                        <CopyableCommandPanel icon={<FaTerminal className="h-3.5 w-3.5" />} label="Codex" text={props.codexSetup} />
                    )}
                </div>
            </div>
        </Alert>
    );
}

function LocalSetupScriptPanel(props: { setupScript: string }) {
    return (
        <div className="rounded border border-white/15 bg-black/20 p-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                <FaDownload className="h-4 w-4" />
                Local setup script
            </div>
            <pre className="max-h-72 overflow-auto rounded bg-black/30 p-3 text-xs">{props.setupScript}</pre>
            <p className="mt-2 text-xs leading-5 text-white/70">
                Codex and Claude Code each read their own variable from <code>{TOKEN_ENV_FILE}</code>, and the script replaces only the
                lines it owns, so setting up one client never overwrites the other client&apos;s token. Both clients resolve the variable at
                run time, so re-running this script with a new token rotates the credential without re-adding the MCP server.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
                <Button icon={<FaCopy />} size="sm" variant="light" onClick={() => copyText(props.setupScript, 'Setup script')}>
                    Copy Script
                </Button>
                <Button
                    icon={<FaDownload />}
                    size="sm"
                    variant="light"
                    onClick={() => downloadTextFile('project314-mcp-setup.sh', props.setupScript)}
                >
                    Download
                </Button>
            </div>
        </div>
    );
}

function CopyableCommandPanel(props: { icon: ReactNode; label: string; text: string }) {
    return (
        <div>
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
                {props.icon}
                {props.label}
            </div>
            <pre className="max-h-64 overflow-auto rounded bg-black/30 p-3 text-xs">{props.text}</pre>
            <Button className="mt-2" icon={<FaCopy />} size="sm" variant="light" onClick={() => copyText(props.text, props.label)}>
                Copy
            </Button>
        </div>
    );
}

function RemoteAppsAndScorecardPanels(props: {
    connectorLoading: boolean;
    connectors: McpConnectorMetadata[];
    readiness: McpClientReadiness[] | undefined;
    scorecardLaunchPrompt: string;
}) {
    const hostedConnectors = props.connectors.filter((connector) => connector.authMode === 'oauth_remote_connector');
    const readinessByTool = new Map(props.readiness?.map((client) => [client.externalTool, client]) ?? []);

    return (
        <div className="mb-6 grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
            <div className="rounded border border-zinc-700 bg-[#1d1d1d] p-4">
                <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-2 text-white">
                            <FaCloud className="h-4 w-4 text-zinc-300" />
                            <h2 className="text-lg font-semibold">Hosted AI Connectors</h2>
                        </div>
                        <p className="mt-1 text-sm leading-6 text-zinc-400">
                            ChatGPT and Claude (web, Desktop, and mobile) should connect through the hosted MCP connector path. The Claude
                            connector is account-level: add it once in claude.ai Settings → Connectors and it works across Claude web,
                            Desktop, and mobile. Do not add the MCP endpoint to claude_desktop_config.json — that file only accepts local
                            stdio servers and silently skips HTTP entries.
                        </p>
                    </div>
                    {props.connectorLoading && <DataLoadingSpinner text="Loading connectors..." />}
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                    {hostedConnectors.map((connector) => {
                        const clientReadiness = readinessByTool.get(connector.externalTool);

                        return (
                            <div className="rounded border border-zinc-700 bg-[#242424] p-3" key={connector.externalTool}>
                                <div className="mb-2 flex items-center justify-between gap-2">
                                    <div className="font-semibold text-white">{connector.label}</div>
                                    <Badge variant="success">OAuth Ready</Badge>
                                </div>
                                <div className="text-xs leading-5 text-zinc-400">
                                    {clientReadiness?.detail ?? 'Hosted connector readiness has not been checked yet.'}
                                </div>
                                {clientReadiness?.matchedOAuthConnection && (
                                    <div className="mt-2 rounded border border-emerald-800/60 bg-emerald-950/20 px-2 py-1 text-xs text-emerald-300">
                                        OAuth connected: {clientReadiness.matchedOAuthConnection.clientName}
                                        {clientReadiness.matchedOAuthConnection.lastUsedAt
                                            ? ` · last used ${clientReadiness.matchedOAuthConnection.lastUsedAt}`
                                            : ''}
                                    </div>
                                )}
                                <div className="mt-3 space-y-2">
                                    <div>
                                        <div className="mb-1 text-xs font-medium text-zinc-300">MCP endpoint</div>
                                        <code className="block break-all rounded bg-black/30 px-2 py-1 text-xs text-zinc-300">
                                            {connector.mcpEndpoint}
                                        </code>
                                    </div>
                                    <div>
                                        <div className="mb-1 text-xs font-medium text-zinc-300">Protected resource metadata</div>
                                        <code className="block break-all rounded bg-black/30 px-2 py-1 text-xs text-zinc-300">
                                            {connector.protectedResourceMetadataUrl}
                                        </code>
                                    </div>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <Button
                                        icon={<FaCopy />}
                                        size="sm"
                                        variant="secondary"
                                        onClick={() => copyText(connector.mcpEndpoint, `${connector.label} MCP endpoint`)}
                                    >
                                        Copy Endpoint
                                    </Button>
                                    <Button
                                        icon={<FaCopy />}
                                        size="sm"
                                        variant="secondary"
                                        onClick={() =>
                                            copyText(connector.protectedResourceMetadataUrl, `${connector.label} resource metadata`)
                                        }
                                    >
                                        Copy Metadata
                                    </Button>
                                    {connector.externalSetupUrl && (
                                        <Button
                                            icon={<FaExternalLinkAlt />}
                                            size="sm"
                                            variant="secondary"
                                            onClick={() => window.open(connector.externalSetupUrl ?? '', '_blank', 'noopener,noreferrer')}
                                        >
                                            Open {connector.label}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    {!props.connectorLoading && hostedConnectors.length === 0 && (
                        <div className="rounded border border-zinc-700 bg-[#242424] p-3 text-sm text-zinc-400">
                            Hosted connector metadata is unavailable.
                        </div>
                    )}
                </div>
            </div>

            <div className="rounded border border-zinc-700 bg-[#1d1d1d] p-4">
                <div className="mb-2 flex items-center gap-2 text-white">
                    <FaCode className="h-4 w-4 text-zinc-300" />
                    <h2 className="text-lg font-semibold">Scorecard Launch</h2>
                </div>
                <p className="mb-3 text-sm leading-6 text-zinc-400">
                    Connected clients can call AI Workbench and scorecard-generation MCP tools. Start scorecard work from the Parameter
                    Discovery page so the session includes the correct experiment, metric, duration, and horizon.
                </p>
                <pre className="max-h-40 overflow-auto rounded bg-black/30 p-3 text-xs text-zinc-200">{props.scorecardLaunchPrompt}</pre>
                <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                        icon={<FaCopy />}
                        size="sm"
                        variant="secondary"
                        onClick={() => copyText(props.scorecardLaunchPrompt, 'Scorecard prompt')}
                    >
                        Copy Prompt
                    </Button>
                    <Button
                        icon={<FaCopy />}
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                            copyText(
                                '/mcp__project314__audit_experiment_scorecard_draft\n/mcp__project314__create_scorecard_from_experiment',
                                'Scorecard slash commands'
                            )
                        }
                    >
                        Copy Commands
                    </Button>
                </div>
            </div>
        </div>
    );
}

function McpTokensTable(props: {
    nonRevokedTokens: McpTokenRecord[];
    nowDateTime: string;
    revokeTokenPending: boolean;
    tokensLoading: boolean;
    onRevokeToken: (tokenId: number) => void;
}) {
    return (
        <div className="rounded border border-zinc-700 bg-[#1d1d1d]">
            <div className="border-b border-zinc-700 px-4 py-3">
                <h2 className="text-lg font-semibold text-white">MCP Tokens</h2>
            </div>
            {props.tokensLoading ? (
                <div className="p-6">
                    <DataLoadingSpinner />
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[960px] text-left text-sm">
                        <thead className="bg-[#252525] text-zinc-300">
                            <tr>
                                <th className="px-4 py-2 font-medium">Name</th>
                                <th className="px-4 py-2 font-medium">Client</th>
                                <th className="px-4 py-2 font-medium">Prefix</th>
                                <th className="px-4 py-2 font-medium">Created</th>
                                <th className="px-4 py-2 font-medium">First Used</th>
                                <th className="px-4 py-2 font-medium">Last Used</th>
                                <th className="px-4 py-2 font-medium">Expires</th>
                                <th className="px-4 py-2 font-medium">Status</th>
                                <th className="px-4 py-2 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-700 text-zinc-200">
                            {props.nonRevokedTokens.map((token) => (
                                <McpTokenRow
                                    key={token.id}
                                    nowDateTime={props.nowDateTime}
                                    revokeTokenPending={props.revokeTokenPending}
                                    token={token}
                                    onRevokeToken={props.onRevokeToken}
                                />
                            ))}
                            {props.nonRevokedTokens.length === 0 && (
                                <tr>
                                    <td className="px-4 py-6 text-center text-zinc-400" colSpan={9}>
                                        No active or expired MCP tokens
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function McpTokenRow(props: {
    nowDateTime: string;
    revokeTokenPending: boolean;
    token: McpTokenRecord;
    onRevokeToken: (tokenId: number) => void;
}) {
    return (
        <tr>
            <td className="px-4 py-3">{props.token.name}</td>
            <td className="px-4 py-3">{getClientLabel(props.token.clientType)}</td>
            <td className="px-4 py-3">
                <code className="rounded bg-black/30 px-2 py-1 text-xs">{props.token.tokenPrefix}</code>
            </td>
            <td className="px-4 py-3">{formatDateTime(props.token.createdDateTime)}</td>
            <td className="px-4 py-3">{formatDateTime(props.token.firstUsedDateTime)}</td>
            <td className="px-4 py-3">{formatDateTime(props.token.lastUsedDateTime)}</td>
            <td className="px-4 py-3">{formatDateTime(props.token.expiresDateTime)}</td>
            <td className="px-4 py-3">{getTokenStatusLabel(props.token, props.nowDateTime)}</td>
            <td className="px-4 py-3">
                <Button
                    icon={<FaTrash />}
                    isLoading={props.revokeTokenPending}
                    size="sm"
                    variant="danger"
                    onClick={() => props.onRevokeToken(props.token.id)}
                >
                    Revoke
                </Button>
            </td>
        </tr>
    );
}

function useMcpAccessTokenMutations(params: {
    setCreatedToken: Dispatch<SetStateAction<string | null>>;
    setCreatedTokenRecord: Dispatch<SetStateAction<McpTokenRecord | null>>;
}) {
    const queryClient = useQueryClient();

    const invalidateTokens = async () => {
        await queryClient.invalidateQueries({
            queryKey: trpc.user.listMcpAccessTokens.queryOptions().queryKey
        });
    };

    const createTokenMutation = useMutation({
        ...trpc.user.createMcpAccessToken.mutationOptions(),
        onSuccess: async (data) => {
            if (!data.success) {
                showToast(data.errorMessages?.[0] ?? 'Failed to create MCP token', 'error');
                return;
            }

            params.setCreatedToken(data.token);
            params.setCreatedTokenRecord(data.tokenRecord);
            await invalidateTokens();
            showToast('MCP token created', 'success');
        },
        onError: (error) => {
            showToast(error.message, 'error');
        }
    });

    const revokeTokenMutation = useMutation({
        ...trpc.user.revokeMcpAccessToken.mutationOptions(),
        onSuccess: async () => {
            await invalidateTokens();
            showToast('MCP token revoked', 'success');
        },
        onError: (error) => {
            showToast(error.message, 'error');
        }
    });

    const dismissSetupMutation = useMutation({
        ...trpc.user.dismissMcpAccessTokenSetup.mutationOptions(),
        onSuccess: async () => {
            await invalidateTokens();
            showToast('Setup guide hidden', 'success');
        },
        onError: (error) => {
            showToast(error.message, 'error');
        }
    });

    return { createTokenMutation, dismissSetupMutation, revokeTokenMutation };
}

function useMcpTokenDerivedState(params: {
    createdTokenRecord: McpTokenRecord | null;
    tokens: McpTokenRecord[] | undefined;
    tokensDataUpdatedAt: number;
}) {
    const allTokens = useMemo(() => params.tokens ?? [], [params.tokens]);
    const nowDateTime = useMemo(() => getDate({ format: 'ymdhms' }), [params.tokensDataUpdatedAt]);
    const nonRevokedTokens = useMemo(() => allTokens.filter((token) => token.revokedDateTime === null), [allTokens]);
    const revokedTokens = useMemo(() => allTokens.filter((token) => token.revokedDateTime !== null), [allTokens]);

    const activeTokens = useMemo(
        () => nonRevokedTokens.filter((token) => !isTokenExpired(token, nowDateTime)),
        [nonRevokedTokens, nowDateTime]
    );

    const connectedTokens = useMemo(
        () => activeTokens.filter((token) => Boolean(token.firstUsedDateTime ?? token.lastUsedDateTime)),
        [activeTokens]
    );

    const { createdTokenRecord } = params;

    const createdTokenFromQuery = createdTokenRecord
        ? (activeTokens.find((token) => token.id === createdTokenRecord.id) ?? createdTokenRecord)
        : null;

    const setupToken = createdTokenFromQuery ?? activeTokens[0] ?? null;
    const setupConnected = setupToken ? Boolean(setupToken.firstUsedDateTime ?? setupToken.lastUsedDateTime) : false;
    const setupDismissed = Boolean(setupToken?.setupDismissedDateTime);
    const shouldShowSetupGuide = !setupConnected && !setupDismissed;
    const setupSteps = buildSetupSteps({ setupToken, setupConnected });

    return { connectedTokens, nonRevokedTokens, nowDateTime, revokedTokens, setupDismissed, setupSteps, setupToken, shouldShowSetupGuide };
}

const DeveloperAccess = () => {
    const { sessionPolicy } = useAuth();
    const [clientType, setClientType] = useState<McpClientType>('codex');
    const [tokenName, setTokenName] = useState(CLIENT_OPTIONS[0]?.defaultName ?? 'Project314 MCP');
    const [expiresInDays, setExpiresInDays] = useState('90');
    const [createdToken, setCreatedToken] = useState<string | null>(null);
    const [createdTokenRecord, setCreatedTokenRecord] = useState<McpTokenRecord | null>(null);

    const endpoint = `${API_DOMAIN}/mcp`;
    const scorecardLaunchPrompt = buildScorecardLaunchPrompt();

    const tokensQuery = useQuery({
        ...trpc.user.listMcpAccessTokens.queryOptions(),
        enabled: sessionPolicy.canManageMcpAccessTokens,
        retry: false
    });

    const clientReadinessQuery = useQuery({
        ...trpc.user.listMcpClientReadiness.queryOptions(),
        enabled: sessionPolicy.canManageMcpAccessTokens,
        retry: false
    });

    const connectorMetadataQuery = useQuery({
        ...trpc.user.listMcpConnectorMetadata.queryOptions(),
        enabled: sessionPolicy.canManageMcpAccessTokens,
        retry: false
    });

    const { createTokenMutation, dismissSetupMutation, revokeTokenMutation } = useMcpAccessTokenMutations({
        setCreatedToken,
        setCreatedTokenRecord
    });

    const handleClientTypeChange = (nextClientType: McpClientType) => {
        setClientType(nextClientType);
        const option = CLIENT_OPTIONS.find((candidate) => candidate.value === nextClientType);

        if (option && CLIENT_OPTIONS.some((candidate) => candidate.defaultName === tokenName)) {
            setTokenName(option.defaultName);
        }
    };

    const { connectedTokens, nonRevokedTokens, nowDateTime, revokedTokens, setupDismissed, setupSteps, setupToken, shouldShowSetupGuide } =
        useMcpTokenDerivedState({
            createdTokenRecord,
            tokens: tokensQuery.data?.tokens,
            tokensDataUpdatedAt: tokensQuery.dataUpdatedAt
        });

    const setupScript =
        createdToken && setupToken
            ? buildSetupScript({ endpoint, token: createdToken, clientType: setupToken.clientType ?? clientType })
            : '';

    const tokenEnvironmentCommand = createdToken ? buildTokenEnvironmentCommand(createdToken, setupToken?.clientType ?? clientType) : '';
    const claudeCommand = buildClaudeCommand(endpoint);
    const codexCommand = buildCodexCommand(endpoint);
    const codexConfig = buildCodexConfig(endpoint);
    const codexSetup = [codexCommand, '', '# Or add this to ~/.codex/config.toml:', codexConfig].join('\n');

    if (!sessionPolicy.canManageMcpAccessTokens) {
        return (
            <UserLayout containerSize="1100px" title="Developer Access - Project 314">
                <SupportRestrictedFeatureCard
                    description="Exit the active support session before managing developer access tokens."
                    title="Developer Access Is Unavailable"
                />
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="1100px" title="Developer Access - Project 314">
            <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-white">Developer Access</h1>
                    <p className="text-sm text-zinc-400">Project314 MCP endpoint: {endpoint}</p>
                </div>
            </div>

            <CreateMcpTokenPanel
                clientType={clientType}
                createTokenPending={createTokenMutation.isPending}
                expiresInDays={expiresInDays}
                tokenName={tokenName}
                onClientTypeChange={handleClientTypeChange}
                onCreateToken={() =>
                    createTokenMutation.mutate({
                        name: tokenName,
                        clientType,
                        expiresInDays: parseExpiresInDays(expiresInDays)
                    })
                }
                onExpiresInDaysChange={setExpiresInDays}
                onTokenNameChange={setTokenName}
            />

            <McpSetupPanel
                connectedTokensCount={connectedTokens.length}
                dismissSetupPending={dismissSetupMutation.isPending}
                setupDismissed={setupDismissed}
                setupSteps={setupSteps}
                setupToken={setupToken}
                shouldShowSetupGuide={shouldShowSetupGuide}
                onDismissSetup={(tokenId) => dismissSetupMutation.mutate({ tokenId })}
            />

            <CreatedTokenPanel
                claudeCommand={claudeCommand}
                codexSetup={codexSetup}
                createdToken={createdToken}
                setupScript={setupScript}
                setupToken={setupToken}
                tokenEnvironmentCommand={tokenEnvironmentCommand}
            />

            <RemoteAppsAndScorecardPanels
                connectorLoading={connectorMetadataQuery.isLoading || clientReadinessQuery.isLoading}
                connectors={connectorMetadataQuery.data?.connectors ?? []}
                readiness={clientReadinessQuery.data?.clients}
                scorecardLaunchPrompt={scorecardLaunchPrompt}
            />

            <McpTokensTable
                nonRevokedTokens={nonRevokedTokens}
                nowDateTime={nowDateTime}
                revokeTokenPending={revokeTokenMutation.isPending}
                tokensLoading={tokensQuery.isLoading}
                onRevokeToken={(tokenId) => revokeTokenMutation.mutate({ tokenId })}
            />

            {revokedTokens.length > 0 && (
                <div className="mt-4 text-sm text-zinc-500">
                    Revoked tokens retained for audit: {revokedTokens.map((token) => token.name).join(', ')}
                </div>
            )}
        </UserLayout>
    );
};

export default DeveloperAccess;
